import React, { useState, useEffect } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../../firebase'
import { useAccessRequests } from '../../hooks/useAccessControl'
import { useToast } from '../../hooks/useToast'
import ToastContainer from '../common/ToastContainer'
import ConfirmDialog from '../common/ConfirmDialog'

export default function ApproveRequestsPage({ user, familyId }) {
  const { requests, loading, approveRequest, rejectRequest } = useAccessRequests(user, familyId)
  const toast = useToast()
  const [processingId, setProcessingId] = useState(null)
  const [confirmAction, setConfirmAction] = useState(null) // { type: 'approve'|'reject', request }
  const [familyDetails, setFamilyDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(true)

  useEffect(() => {
    const fetchFamilyDetails = async () => {
      if (!familyId) return
      
      try {
        const familyRef = doc(db, 'families', familyId)
        const familyDoc = await getDoc(familyRef)
        
        if (familyDoc.exists()) {
          setFamilyDetails(familyDoc.data())
        }
      } catch (err) {
        console.error('Error fetching family details:', err)
      } finally {
        setLoadingDetails(false)
      }
    }

    fetchFamilyDetails()
  }, [familyId])

  const handleApprove = async (request) => {
    setProcessingId(request.id)
    try {
      await approveRequest(request.id, request.userId, request.email)
      toast.success(`Approved access for ${request.email}`)
    } catch (err) {
      toast.error('Failed to approve request')
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (request) => {
    setProcessingId(request.id)
    try {
      await rejectRequest(request.id)
      toast.success(`Rejected request from ${request.email}`)
    } catch (err) {
      toast.error('Failed to reject request')
    } finally {
      setProcessingId(null)
    }
  }

  const copyFamilyId = async () => {
    if (familyId) {
      try {
        await navigator.clipboard.writeText(familyId)
        toast.success('Family ID copied to clipboard!')
      } catch {
        toast.error('Failed to copy to clipboard')
      }
    }
  }

  if (loading || loadingDetails) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="glass-card p-8 flex flex-col items-center gap-4">
          <svg className="animate-spin h-12 w-12 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />
      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction?.type === 'approve') handleApprove(confirmAction.request)
          else if (confirmAction?.type === 'reject') handleReject(confirmAction.request)
        }}
        title={confirmAction?.type === 'approve' ? 'Approve Access' : 'Reject Access'}
        message={confirmAction ? (
          confirmAction.type === 'approve'
            ? `Approve access for ${confirmAction.request.email}?`
            : `Reject access request from ${confirmAction.request.email}?`
        ) : ''}
        confirmLabel={confirmAction?.type === 'approve' ? 'Approve' : 'Reject'}
        danger={confirmAction?.type === 'reject'}
      />
      
      {/* Family Details Card */}
      {familyDetails && (
        <div className="glass-card">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {familyDetails.name}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Created on {new Date(familyDetails.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="glass-badge">
                {familyDetails.memberCount} {familyDetails.memberCount === 1 ? 'member' : 'members'}
              </div>
            </div>

            {/* Family ID Section */}
            <div className="glass-panel p-4 mt-4">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                Family ID - Share with family members to invite them
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-base font-mono font-bold text-gray-900 dark:text-white bg-white/50 dark:bg-gray-800/50 px-4 py-2.5 rounded-lg">
                  {familyId}
                </code>
                <button
                  onClick={copyFamilyId}
                  className="glass-btn-secondary glass-btn-sm whitespace-nowrap"
                  title="Copy Family ID"
                >
                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy ID
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent mb-2">
          Access Requests
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Review and approve access requests for this family
        </p>
      </div>

      {requests.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
            No Pending Requests
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            All access requests have been reviewed
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div key={request.id} className="glass-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                      {request.email?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {request.email}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Requested {request.requestedAt ? new Date(request.requestedAt).toLocaleDateString() : 'recently'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="glass-panel p-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Their Note:
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {request.note || 'No note provided'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setConfirmAction({ type: 'approve', request })}
                    disabled={processingId === request.id}
                    className="glass-btn-primary glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {processingId === request.id ? (
                      <>
                        <svg className="animate-spin h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Approve
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setConfirmAction({ type: 'reject', request })}
                    disabled={processingId === request.id}
                    className="glass-btn-danger glass-btn-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
